import React, { useMemo } from 'react';
import { CheckCircle, Clock, Calendar, Lock, CircleAlert, FileText, Globe, X, Play, Award, Star, XCircle, Brain, RefreshCcw } from 'lucide-react';
import { Button } from '../common/Button';
import { SeriesRound } from '../../types/interviews';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { DurationDisplay } from '../common/DurationDisplay';

interface InterviewRoundCardProps {
  round: SeriesRound;
  roundNumber: number;
  totalRounds: number;
  isEnrolled: boolean;
  onSchedule: () => void;
  onViewFeedback?: () => void;
  onCancel?: () => void;
  onJoin?: () => void;
  previousRoundCompleted?: boolean;
  isActive?: boolean;
  cancelLoading?: boolean;
}

export const InterviewRoundCard: React.FC<InterviewRoundCardProps> = ({
  round,
  roundNumber,
  totalRounds,
  isEnrolled,
  onSchedule,
  onViewFeedback,
  onCancel,
  onJoin,
  previousRoundCompleted = true,
  isActive = true,
  cancelLoading = false
}) => {
  const { isMobile: isMobileView } = useResponsive();
  
  // Check if the interview is joinable (within 10 minutes of scheduled time)
  const isJoinable = useMemo(() => {
    if (round.status !== 'not_started' || !round.scheduledDate) return false;
    
    const scheduledTime = new Date(round.scheduledDate).getTime();
    const currentTime = new Date().getTime();
    const tenMinutesInMs = 10 * 60 * 1000;
    
    // Interview is joinable if current time is between scheduled time and 10 minutes after
    return currentTime >= scheduledTime && currentTime <= scheduledTime + tenMinutesInMs;
  }, [round.status, round.scheduledDate]);
  
  // Get status badge based on round status
  const getStatusBadge = () => {
    console.log(`Round ${roundNumber} status: ${round.status}`);
    switch (round.status) {
      case 'completed':
        return {
          icon: <CheckCircle size={14} className="mr-1 text-green-500" />,
          text: 'Completed',
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          textColor: 'text-green-700 dark:text-green-400'
        };
      case 'scheduled':
        return {
          icon: <Calendar size={14} className="mr-1 text-blue-500" />,
          text: 'Scheduled',
          bgColor: 'bg-amber-100 dark:bg-amber-900/20',
          textColor: 'text-amber-700 dark:text-amber-400'
        };
      case 'not_started':
        return {
          icon: <CircleAlert size={14} className="mr-1 text-blue-500" />,
          text: 'Available',
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          textColor: 'text-blue-700 dark:text-blue-400'
        };
      case 'in_progress':
        return {
          icon: <Play size={14} className="mr-1 text-purple-500" />,
          text: 'In Progress',
          bgColor: 'bg-purple-100 dark:bg-purple-900/20',
          textColor: 'text-purple-700 dark:text-purple-400'
        };
      case 'cancelled':
        return {
          icon: <XCircle size={14} className="mr-1 text-red-500" />,
          text: 'Cancelled',
          bgColor: 'bg-red-100 dark:bg-red-900/20',
          textColor: 'text-red-700 dark:text-red-400'
        };
      case 'expired':
        return {
          icon: <XCircle size={14} className="mr-1 text-red-500" />,
          text: 'No Appearance',
          bgColor: 'bg-red-100 dark:bg-red-900/20',
          textColor: 'text-red-700 dark:text-red-400'
        };
      case 'locked':
        return {
          icon: <Lock size={14} className="mr-1 text-gray-500" />,
          text: 'Locked',
          bgColor: 'bg-gray-200 dark:bg-gray-800',
          textColor: 'text-gray-700 dark:text-gray-400'
        };
      default:
        return {
          icon: <CircleAlert size={14} className="mr-1 text-blue-500" />,
          text: 'Available',
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          textColor: 'text-blue-700 dark:text-blue-400'
        };
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not scheduled';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  
  // Get action button based on round status and user enrollment
  const getActionButton = () => {
    if (!isEnrolled) return <Button 
      variant="outline"
      size="sm" 
      className="py-3 px-5"
      disabled={true}
      onClick={onSchedule}>Join the series first</Button>;
    
    console.log(`Round ${roundNumber} status: ${round.status}, previousRoundCompleted: ${previousRoundCompleted}`);
    
    switch (round.status) {
      case 'completed':
        return (
          <div className="flex flex-col space-x-2 gap-2 mt-1">
            {round.feedback && (
              <Button 
                variant="secondary" 
                size="sm"
                className="py-3 px-5"
                onClick={onViewFeedback}
                leftIcon={<FileText size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
              >
                View Feedback
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              className="py-3 px-5"
              onClick={onJoin}
              leftIcon={<RefreshCcw size={14} className="text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark" />}
            >
              Reattempt
            </Button>
          </div>
        );
      case 'scheduled':
        if (isJoinable && onJoin) {
          return (
            <div className="flex space-x-2 gap-1">
              <Button 
                variant="primary" 
                size="sm" 
                className="py-3 px-5"
                onClick={onJoin}
                disabled={cancelLoading}
                leftIcon={<Play size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
              >
                Join Now
              </Button>
              {isActive && onCancel && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="py-3 px-5"
                  onClick={onCancel}
                  loading={cancelLoading}
                  disabled={cancelLoading}
                  leftIcon={<X size={14} className="text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark" />}
                >
                  Cancel
                </Button>
              )}
            </div>
          );
        }
        return (
          <div className="flex space-x-2">
            <Button 
              variant="secondary" 
              size="sm" 
              className="py-3 px-5"
              onClick={onSchedule}
              leftIcon={<Calendar size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
            >
              Reschedule
            </Button>
            {isActive && onCancel && (
              <Button 
                variant="outline" 
                size="sm" 
                className="py-3 px-5"
                onClick={onCancel}
                loading={cancelLoading}
                disabled={cancelLoading}
                leftIcon={<X size={14} className="text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark" />}
              >
                Cancel
              </Button>
            )}
          </div>
        );
      case 'not_started':
        // Only enable the schedule button if previous round is completed or this is the first round
        return (
          <Button 
            variant="primary" 
            size="sm" 
            className="py-3 px-5"
            onClick={onSchedule}
            disabled={!previousRoundCompleted}
            leftIcon={<Calendar size={14} className={`${previousRoundCompleted ? 'text-white' : 'text-text-primary-light dark:text-text-primary-dark'}`} />}
          >
            {!previousRoundCompleted ? 'Complete Above Round' : 'Schedule'}
          </Button>
        );
      case 'in_progress':
        return (
          <div className="flex flex-col space-y-2 mt-1 gap-1">
            <Button 
              variant="primary" 
              size="sm" 
              className="py-3 px-5"
              onClick={onJoin}
              disabled={cancelLoading}
              leftIcon={<Play size={14} className="text-white" />}
            >
              Join Interview
            </Button>
            {isActive && onCancel && (
              <Button 
                variant="outline" 
                size="sm" 
                className="py-3 px-5"
                onClick={onCancel}
                loading={cancelLoading}
                disabled={cancelLoading}
                leftIcon={<X size={14} className="text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark" />}
              >
                Cancel
              </Button>
            )}
          </div>
        );

      case 'locked':
        return (
          <Button 
            variant="outline" 
            size="sm"
            className="py-3 px-5"
            disabled
            leftIcon={<Lock size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
            >
            Locked
          </Button>
        );
      case 'cancelled':
        return (
          <Button 
              variant="primary" 
              size="sm" 
              className="py-3 px-5"
              onClick={onSchedule}
              leftIcon={<Calendar size={14} className="text-white" />}
            >
             Reschedule
            </Button>
        );
      case 'expired':
        return (
          <Button 
            variant="primary" 
            size="sm" 
            className="py-3 px-5"
            onClick={onSchedule}
            leftIcon={<Calendar size={14} className="text-white" />}
          >
            Reschedule
          </Button>
        );
        
      default:
        // Default to available if previous round is completed
        if (previousRoundCompleted) {
          return (
            <div className="flex flex-col space-y-2 mt-1 gap-1">
              <Button 
                variant="primary" 
                size="sm" 
                className="py-3 px-5"
                onClick={onJoin}
                leftIcon={<Play size={14} className="text-white" />}
              >
                Start Now
              </Button>
              <Button 
                variant="secondary" 
                size="sm" 
                className="py-3 px-5"
                onClick={onSchedule}
                leftIcon={<Calendar size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
            >
                Schedule for later
              </Button>
            </div>
          );
        } else {
          return (
            <Button 
              variant="outline" 
              size="sm"
              className="py-3 px-5"
              disabled
              leftIcon={<Lock size={14} className="text-text-primary-light dark:text-text-primary-dark" />}
            >
              Complete Previous Round
            </Button>
          );
        }
    }
  };
  
  const statusBadge = getStatusBadge();
  
  return (
    <div className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl p-4 md:p-6 hover:border-primary-light/30 dark:hover:border-primary-dark/30 transition-all">
      <div className="flex flex-col md:flex-row md:items-center">
        <div className="flex-1">
          {isMobileView && (
              <div className={`inline-block justify-center w-fit px-3 py-1 rounded-full mb-2 text-xs font-heading font-medium ${statusBadge.bgColor} ${statusBadge.textColor} flex items-center`}>
                {statusBadge.icon}
                {statusBadge.text}
              </div>
            )}
          <div className="flex items-center mb-3">
            {!isMobileView && (
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark font-medium mr-3">
                {roundNumber}
              </div>
            )}
            
            <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
              {isMobileView ? `Round ${roundNumber}: ${round.title}` : round.title}
            </h3>
            {!isMobileView && (
              <div className={`ml-auto md:ml-4 px-3 py-1 rounded-full text-xs font-heading font-medium ${statusBadge.bgColor} ${statusBadge.textColor} flex items-center`}>
                {statusBadge.icon}
                {statusBadge.text}
              </div>
            )}
          </div>
          
          <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 text-sm max-w-3xl">
            {round.description}
          </p>
          
          <div className="flex flex-wrap gap-3 mb-4 md:mb-0">
            {/* Duration display hidden as it can be confusing for users
            <DurationDisplay 
              minutes={round.duration || 30}
              clockSize={13}
              className="bg-primary-light/5 dark:bg-primary-dark/5 px-2 py-1 rounded-md border border-primary-light/10 dark:border-primary-dark/10"
              showBackground={false}
            /> */}
            
            <div className="flex items-center text-xs bg-green-500/10 dark:bg-green-400/10 px-2.5 py-1 rounded-xl border border-green-500/20 dark:border-green-400/20">
              <Globe size={13} className="mr-1 text-green-500 dark:text-green-400" />
              <span className="text-green-700 dark:text-green-400 font-medium">{round.languageName || round.language}</span>
            </div>

            <div className={`flex items-center text-xs ${round.difficulty <= 2 ? "bg-green-600/15 dark:bg-green-400/15" : round.difficulty <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" : "bg-red-600/15 dark:bg-red-400/15"} px-2.5 py-1 rounded-xl border ${round.difficulty <= 2 ? "border-green-600/10 dark:border-green-400/10" : round.difficulty <= 4 ? "border-amber-600/10 dark:border-amber-400/10" : "border-red-600/10 dark:border-red-400/10"}`}>
            <Brain size={13} className={`mr-1 ${round.difficulty <= 2 ? "text-green-600 dark:text-green-400" : round.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`} />
              <span className={`mr-1 ${round.difficulty <= 2 ? "text-green-600 dark:text-green-400" : round.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>Difficulty:</span>
              <span className={`font-medium ${
                round.difficulty <= 2 ? "text-green-600 dark:text-green-400" : 
                round.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : 
                "text-red-600 dark:text-red-400"
              }`}>
                {round.difficulty <= 2 ? "Easy" : 
                 round.difficulty <= 4 ? "Medium" : 
                 "Hard"}
              </span>
            </div>
            {round.scheduledDate && (
              <div className="flex items-center text-xs bg-primary-light/5 dark:bg-primary-dark/5 px-2 py-1 rounded-md border border-primary-light/10 dark:border-primary-dark/10">
                <Calendar size={13} className="mr-1 text-primary-light dark:text-primary-dark" />
                <span>{formatDate(round.scheduledDate)}</span>
              </div>
            )}
            
            {round.score !== undefined && (
              <div className="flex items-center text-xs bg-green-50 dark:bg-green-900/10 px-2 py-1 rounded-md border border-green-100 dark:border-green-900/20 text-green-700 dark:text-green-400">
                <CheckCircle size={13} className="mr-1" />
                <span>Score: {round.score}%</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-shrink-0 mt-4 md:mt-0 md:ml-6">
          {getActionButton()}
        </div>
      </div>
      
      {roundNumber < totalRounds && !isMobileView && (
        <div className="mt-4 hidden md:block">
          <div className="w-2 h-8 border-l-2 border-dashed border-primary-light/30 dark:border-primary-dark/30 ml-4"></div>
        </div>
      )}
    </div>
  );
};

export default InterviewRoundCard; 